import { useEffect, useMemo, useState } from 'react';
import { useTheme } from '../../context/ThemeContext';
import {
  adminWholesale,
  type WholesaleInquiryOut,
  type WholesaleStatus,
} from '../../api/admin';
import {
  Boxes, Clock, Phone, CheckCircle, Pencil, Trash2, X, Check,
  Mail, MapPin, Package, Calendar, ChevronDown, ChevronUp, Search,
} from 'lucide-react';

const STATUS_CONFIG: Record<WholesaleStatus, { bg: string; text: string; border: string; icon: React.ElementType; label: string }> = {
  new:       { bg: 'bg-yellow-500/10',  text: 'text-yellow-500',  border: 'border-yellow-500/30',  icon: Clock,       label: 'New' },
  contacted: { bg: 'bg-blue-500/10',    text: 'text-blue-500',    border: 'border-blue-500/30',    icon: Phone,       label: 'Contacted' },
  closed:    { bg: 'bg-emerald-500/10', text: 'text-emerald-500', border: 'border-emerald-500/30', icon: CheckCircle, label: 'Closed' },
};

const STATUSES: WholesaleStatus[] = ['new', 'contacted', 'closed'];

interface EditForm {
  name: string;
  business_name: string;
  phone: string;
  email: string;
  city: string;
  products: string;
  quantity: number;
  message: string;
  status: WholesaleStatus;
}

export default function WholesaleInquiriesPage() {
  const { isDark } = useTheme();
  const [list, setList] = useState<WholesaleInquiryOut[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [form, setForm] = useState<EditForm | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | WholesaleStatus>('all');

  useEffect(() => {
    adminWholesale.list().then(setList).catch(console.error).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    let rows = [...list];
    if (search) {
      const q = search.toLowerCase().trim();
      const digitQ = q.replace(/\D/g, '');
      rows = rows.filter(i =>
        i.name.toLowerCase().includes(q) ||
        i.business_name.toLowerCase().includes(q) ||
        i.email.toLowerCase().includes(q) ||
        i.city.toLowerCase().includes(q) ||
        i.products.toLowerCase().includes(q) ||
        (digitQ.length > 0 && i.phone.replace(/\D/g, '').includes(digitQ))
      );
    }
    if (statusFilter !== 'all') rows = rows.filter(i => i.status === statusFilter);
    return rows;
  }, [list, search, statusFilter]);

  const startEdit = (inq: WholesaleInquiryOut) => {
    setForm({
      name: inq.name,
      business_name: inq.business_name,
      phone: inq.phone,
      email: inq.email,
      city: inq.city,
      products: inq.products,
      quantity: inq.quantity,
      message: inq.message,
      status: inq.status,
    });
    setEditingId(inq.id);
    setError(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(null);
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingId || !form) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await adminWholesale.update(editingId, form);
      setList(prev => prev.map(i => (i.id === editingId ? updated : i)));
      cancelEdit();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save inquiry');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const quickStatus = async (inq: WholesaleInquiryOut, status: WholesaleStatus) => {
    if (status === inq.status) return;
    try {
      const updated = await adminWholesale.update(inq.id, { status });
      setList(prev => prev.map(i => (i.id === inq.id ? updated : i)));
    } catch (err) {
      console.error(err);
    }
  };

  const deleteInquiry = async (inq: WholesaleInquiryOut) => {
    if (!confirm(`Delete inquiry from ${inq.name} (${inq.business_name})?`)) return;
    try {
      await adminWholesale.delete(inq.id);
      setList(prev => prev.filter(i => i.id !== inq.id));
    } catch (err) {
      console.error(err);
    }
  };

  const inputCls = `w-full px-3 py-2 rounded-lg border text-sm outline-none ${
    isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-gray-50 border-gray-200'
  }`;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  const counts = {
    new: list.filter(i => i.status === 'new').length,
    contacted: list.filter(i => i.status === 'contacted').length,
    closed: list.filter(i => i.status === 'closed').length,
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Boxes size={24} /> Wholesale Inquiries</h1>
          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            {counts.new} new &middot; {counts.contacted} contacted &middot; {counts.closed} closed
          </p>
        </div>
      </div>

      {/* Status filter pills */}
      <div className="flex flex-wrap gap-2">
        <button onClick={() => setStatusFilter('all')}
          className={`px-4 py-1.5 rounded-full text-xs font-semibold border transition-all ${statusFilter === 'all' ? 'bg-emerald-500 text-white border-emerald-500 shadow-lg shadow-emerald-500/20' : isDark ? 'border-gray-700 text-gray-400 hover:border-gray-500' : 'border-gray-200 text-gray-500 hover:border-gray-300'}`}>
          All ({list.length})
        </button>
        {STATUSES.map(s => {
          const cfg = STATUS_CONFIG[s];
          return (
            <button key={s} onClick={() => setStatusFilter(s)}
              className={`px-4 py-1.5 rounded-full text-xs font-semibold border transition-all ${statusFilter === s ? `${cfg.bg} ${cfg.text} ${cfg.border} shadow-md` : isDark ? 'border-gray-700 text-gray-400 hover:border-gray-500' : 'border-gray-200 text-gray-500 hover:border-gray-300'}`}>
              {cfg.label} ({counts[s]})
            </button>
          );
        })}
      </div>

      {/* Search */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search size={16} className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
          <input type="text" placeholder="Search by name, business, email, phone, city..." value={search} onChange={e => setSearch(e.target.value)}
            className={`w-full pl-9 pr-4 py-2.5 rounded-lg border text-sm outline-none transition-colors ${isDark ? 'bg-gray-900 border-gray-800 text-white placeholder-gray-500 focus:border-gray-600' : 'bg-white border-gray-200 placeholder-gray-400 focus:border-gray-300'}`} />
        </div>
      </div>

      {/* Edit form */}
      {editingId && form && (
        <form onSubmit={handleSubmit} className={`rounded-xl border p-6 space-y-4 ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}>
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-lg">Edit Inquiry</h2>
            <button type="button" onClick={cancelEdit} className="text-gray-400 hover:text-gray-300" title="Cancel">
              <X size={18} />
            </button>
          </div>
          {error && (
            <div className="px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/30 text-red-500 text-sm">{error}</div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Name</label>
              <input type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className={inputCls} required maxLength={100} />
            </div>
            <div>
              <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Business</label>
              <input type="text" value={form.business_name} onChange={e => setForm({ ...form, business_name: e.target.value })} className={inputCls} required maxLength={150} />
            </div>
            <div>
              <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Status</label>
              <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value as WholesaleStatus })} className={inputCls}>
                {STATUSES.map(s => <option key={s} value={s}>{STATUS_CONFIG[s].label}</option>)}
              </select>
            </div>
            <div>
              <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Phone</label>
              <input type="text" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className={inputCls} required maxLength={20} />
            </div>
            <div>
              <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Email</label>
              <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className={inputCls} required maxLength={150} />
            </div>
            <div>
              <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>City</label>
              <input type="text" value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} className={inputCls} required maxLength={100} />
            </div>
            <div>
              <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Products</label>
              <input type="text" value={form.products} onChange={e => setForm({ ...form, products: e.target.value })} className={inputCls} required maxLength={300} />
            </div>
            <div>
              <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Quantity (pcs)</label>
              <input type="number" value={form.quantity} onChange={e => setForm({ ...form, quantity: Number(e.target.value) || 0 })} min={100} step={1} className={inputCls} required />
            </div>
          </div>
          <div>
            <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Message</label>
            <textarea value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} rows={3} className={`${inputCls} resize-none`} required maxLength={2000} />
          </div>
          <button type="submit" disabled={saving} className="flex items-center gap-1.5 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors">
            <Check size={16} /> {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </form>
      )}

      {/* Inquiries table */}
      <div className={`rounded-xl border overflow-x-auto ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
        <div className="min-w-[760px]">
          {/* Header */}
          <div className={`grid grid-cols-[2fr_1.5fr_0.8fr_1.2fr_auto_auto] gap-4 px-4 py-3 text-xs font-semibold uppercase tracking-wider ${isDark ? 'bg-gray-900/80 text-gray-500 border-b border-gray-800' : 'bg-gray-50 text-gray-400 border-b border-gray-200'}`}>
            <div>Customer</div><div>Business</div><div>Qty</div><div>Status</div><div>Edit</div><div className="w-6"></div>
          </div>

          {filtered.length === 0 && (
            <div className={`text-center py-16 ${isDark ? 'bg-gray-900' : 'bg-white'}`}>
              <Boxes size={48} className={`mx-auto mb-3 ${isDark ? 'text-gray-700' : 'text-gray-200'}`} />
              <p className={`text-sm font-medium ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                {list.length === 0 ? 'No wholesale inquiries yet.' : 'No inquiries match your filters.'}
              </p>
            </div>
          )}

          {filtered.map(inq => {
            const cfg = STATUS_CONFIG[inq.status];
            const isExpanded = expandedId === inq.id;
            return (
              <div key={inq.id} className={`${isDark ? 'bg-gray-900' : 'bg-white'} ${isExpanded ? '' : isDark ? 'hover:bg-gray-800/40' : 'hover:bg-gray-50'} transition-colors border-b ${isDark ? 'border-gray-800/50' : 'border-gray-100'}`}>
                <div className="grid grid-cols-[2fr_1.5fr_0.8fr_1.2fr_auto_auto] gap-4 px-4 py-3.5 items-center">
                  <div className="cursor-pointer min-w-0" onClick={() => setExpandedId(isExpanded ? null : inq.id)}>
                    <p className="font-semibold text-sm truncate">{inq.name}</p>
                    <p className={`text-xs truncate ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{inq.email}</p>
                  </div>
                  <div className="cursor-pointer min-w-0" onClick={() => setExpandedId(isExpanded ? null : inq.id)}>
                    <p className="text-sm truncate">{inq.business_name}</p>
                    <p className={`text-xs truncate ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{inq.city}</p>
                  </div>
                  <div>
                    <p className="font-bold text-sm">{inq.quantity}</p>
                    <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>pcs</p>
                  </div>
                  <div onClick={e => e.stopPropagation()}>
                    <select
                      value={inq.status}
                      onChange={e => quickStatus(inq, e.target.value as WholesaleStatus)}
                      className={`px-2.5 py-1.5 rounded-lg border text-xs font-semibold outline-none cursor-pointer ${cfg.bg} ${cfg.text} ${cfg.border}`}
                    >
                      {STATUSES.map(s => <option key={s} value={s}>{STATUS_CONFIG[s].label}</option>)}
                    </select>
                  </div>
                  <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                    <button onClick={() => startEdit(inq)} className={`p-1.5 rounded-lg transition-colors ${isDark ? 'hover:bg-gray-700 text-gray-400 hover:text-blue-400' : 'hover:bg-gray-200 text-gray-500 hover:text-blue-500'}`} title="Edit">
                      <Pencil size={14} />
                    </button>
                    <button onClick={() => deleteInquiry(inq)} className={`p-1.5 rounded-lg transition-colors ${isDark ? 'hover:bg-gray-700 text-gray-400 hover:text-red-400' : 'hover:bg-gray-200 text-gray-500 hover:text-red-500'}`} title="Delete">
                      <Trash2 size={14} />
                    </button>
                  </div>
                  <div className="cursor-pointer p-1 rounded-lg" onClick={() => setExpandedId(isExpanded ? null : inq.id)}>
                    {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </div>
                </div>

                {isExpanded && (
                  <div className={`px-4 pb-4 pt-1 space-y-2 text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    <p className="flex items-center gap-2"><Phone size={13} className="shrink-0" /> {inq.phone}</p>
                    <p className="flex items-center gap-2"><Mail size={13} className="shrink-0" /> <a href={`mailto:${inq.email}`} className="text-emerald-500 hover:underline break-all">{inq.email}</a></p>
                    <p className="flex items-center gap-2"><MapPin size={13} className="shrink-0" /> {inq.city}</p>
                    <p className="flex items-center gap-2"><Package size={13} className="shrink-0" /> {inq.products}</p>
                    <p className="flex items-center gap-2"><Calendar size={13} className="shrink-0" /> {new Date(inq.created_at).toLocaleString('en-IN')}</p>
                    <div className={`rounded-lg p-3 ${isDark ? 'bg-gray-800 text-gray-300' : 'bg-gray-50 text-gray-700'}`}>{inq.message}</div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
